/** @module                             React */

import { useState }                     from "react";

/** @module                             i18n */

import { useTranslation }               from "react-i18next";

/** @module                             React-Icons */

import { MdAdd, MdDeleteOutline }       from "react-icons/md";

/** @module                             Components */

import { Input, LabeledInput }          from "../../Global/Forms/Inputs_Spread";

import ControlledCheckbox               from "../../Global/Forms/ControlledCheckbox";

/** @module                             Helpers */

import { 
    
    isGroup, 
    
    isRowList, 
    
    humanize, 
    
    blankRow,
    
    omit,
    
    normalizeCountryCode,
    
    isCountryCode }                     from "./Pricing_Utils";

const 

COUNTRY_PRICE_KEYS = [ "base_prices", "distance_surcharges" ],

BLANK_COUNTRY = {

    available: false,

    free_delivery_in_listed_cities: false,

    item_count_brackets: [ { max_items: null, price: 0 } ]

},

/**
 * 
 * @param { Function } t
 * 
 * @param { String } key
 * 
 * @returns a translated label, falling back to the settings key itself
 * 
 */

labelFor = ( t, key ) => t ( `delivery_pricing_${ key }`, t ( key, humanize ( key ) ) ),

/**
 * 
 * @param { * } value
 * 
 * @returns a settings value as editable text
 * 
 */

toText = value => ( value === null || value === undefined ) ? "" : String ( value ),

isCountryGroup = ( key, value ) => key === "countries" && isGroup ( value ),

isCountryPriceMap = ( key, value ) => COUNTRY_PRICE_KEYS.includes ( key ) && isGroup ( value ),

isSurchargeTable = ( key, value ) => key === "type_surcharges" && isGroup ( value ),

/**
 * 
 * @returns a single editable setting
 * 
 * @description Booleans get a checkbox, everything else a text field, because a number input
 * cannot hold an in-progress value like "5." or an intentionally empty "no limit".
 * 
 */

Leaf = ( { name, path, value, onChange, disabled, compact = false } ) => { const { t } = useTranslation ();

    if ( typeof value === "boolean" ) return ( <ControlledCheckbox 
    
        id={ path.join ( "-" ) }

        text={ labelFor ( t, name ) }

        state={ value }

        disabled={ disabled }

        action={ event => onChange ( path, event.target.checked ) } /> );

    if ( compact ) return ( <Input 

        type="text"

        inputMode="decimal"

        placeholder={ labelFor ( t, name ) }

        value={ toText ( value ) }

        disabled={ disabled }

        action={ event => onChange ( path, event.target.value ) } /> );

    return ( <LabeledInput 

        type="text"

        inputMode="decimal"

        label={ `delivery_pricing_${ name }` }

        value={ toText ( value ) }

        disabled={ disabled }

        action={ event => onChange ( path, event.target.value ) } /> );

},

/**
 * 
 * @returns a comma separated editor for a plain list of values, eg. free delivery cities
 * 
 */

WordList = ( { name, path, value, onChange, disabled } ) => { const { t } = useTranslation ();

    /** @returns */

    return ( <div className="pricing-group">

        <LabeledInput 

            type="text"

            label={ `delivery_pricing_${ name }` }

            desc="delivery_pricing_comma_separated"

            value={ value.join ( ", " ) }

            disabled={ disabled }

            action={ event => onChange ( 
                
                path, 
                
                event.target.value.split ( "," ).map ( entry => entry.trim () ).filter ( Boolean ) 
                
            ) } />

    </div> );

},

/**
 * 
 * @returns a table editor for bracket lists such as weight or item count tiers
 * 
 */

RowList = ( { name, path, value, onChange, disabled, framed = true } ) => { const { t } = useTranslation (),

    columns = Object.keys ( value [ 0 ] || {} ),

    table = ( <div className="pricing-table-wrap">

        <table className="pricing-table">

            <thead>

                <tr>

                    { columns.map ( column => <th key={ column }>{ labelFor ( t, column ) }</th> ) }

                    <th className="pricing-table__actions" />

                </tr>

            </thead>

            <tbody>

                { value.map ( ( row, index ) => <tr key={ index }>

                    { columns.map ( column => <td key={ column }>

                        <Leaf 
                        
                            name={ column } 
                            
                            path={ [ ...path, index, column ] } 
                            
                            value={ row [ column ] } 
                            
                            onChange={ onChange } 
                            
                            disabled={ disabled }
                            
                            compact />

                    </td> ) }

                    <td className="pricing-table__actions">

                        <button 
                        
                            type="button" 
                            
                            className="icon" 
                            
                            disabled={ disabled || value.length <= 1 }
                            
                            title={ t ( "delete", "Delete" ) }
                            
                            onClick={ () => onChange ( path, value.filter ( ( _row, position ) => position !== index ) ) }>

                            <MdDeleteOutline />

                        </button>

                    </td>

                </tr> ) }

            </tbody>

        </table>

        <button 
        
            type="button" 
            
            className="pricing-table__add" 
            
            disabled={ disabled }
            
            onClick={ () => onChange ( path, [ ...value, blankRow ( value ) ] ) }>

            <MdAdd />

            <span>

                { t ( "delivery_pricing_add_row", "Add row" ) }

            </span>

        </button>

    </div> );

    if ( ! framed ) return table;

    /** @returns */

    return ( <div className="pricing-group">

        <div className="pricing-group__title">

            { labelFor ( t, name ) }

        </div>

        { table }

    </div> );

},

/**
 * 
 * @returns product type surcharges as a two-column table, with nested vehicle types indented
 * 
 */

TypeSurcharges = ( { name, path, value, onChange, disabled } ) => { const { t } = useTranslation (),

    rows = Object.entries ( value ).flatMap ( ( [ key, child ] ) => {

        if ( isGroup ( child ) ) return [

            { key, path: [ ...path, key ], label: labelFor ( t, key ), value: null, heading: true },

            ...Object.entries ( child ).map ( ( [ nested, amount ] ) => ( {

                key: `${ key }.${ nested }`,

                path: [ ...path, key, nested ],

                label: labelFor ( t, nested ),

                value: amount,

                nested: true

            } ) )

        ];

        return [ { key, path: [ ...path, key ], label: labelFor ( t, key ), value: child } ];

    } );

    /** @returns */

    return ( <div className="pricing-group">

        <div className="pricing-group__title">

            { labelFor ( t, name ) }

        </div>

        <div className="pricing-table-wrap">

            <table className="pricing-table">

                <thead>

                    <tr>

                        <th>

                            { t ( "delivery_pricing_product_type" ) }

                        </th>

                        <th>

                            { t ( "delivery_pricing_price" ) }

                        </th>

                    </tr>

                </thead>

                <tbody>

                    { rows.map ( row => <tr key={ row.key } className={ row.heading ? "is-heading" : row.nested ? "is-nested" : "" }>

                        <th scope="row">

                            { row.label }

                        </th>

                        <td>

                            { ! row.heading && <Input 
                            
                                type="text" 
                                
                                inputMode="decimal" 
                                
                                value={ toText ( row.value ) } 
                                
                                disabled={ disabled } 
                                
                                action={ event => onChange ( row.path, event.target.value ) } /> }

                        </td>

                    </tr> ) }

                </tbody>

            </table>

        </div>

    </div> );

},

/**
 * 
 * @returns one card per country so availability, free-delivery rules and brackets sit together
 * 
 */

CountryPriceMap = ( { name, path, value, onChange, disabled } ) => { const { t } = useTranslation (),

    codes = Object.keys ( value );

    /** @returns */

    return ( <div className="pricing-group">

        <div className="pricing-group__title">

            { labelFor ( t, name ) }

        </div>

        <div className="pricing-table-wrap">

            <table className="pricing-table">

                <thead>

                    <tr>

                        <th>

                            { t ( "delivery_pricing_country_code" ) }

                        </th>

                        <th>

                            { t ( "delivery_pricing_price" ) }

                        </th>

                        <th className="pricing-table__actions" />

                    </tr>

                </thead>

                <tbody>

                    { codes.map ( code => <tr key={ code }>

                        <th scope="row">

                            { t ( `delivery_pricing_country_${ code.toLowerCase () }`, code ) }

                        </th>

                        <td>

                            <Input 
                            
                                type="text" 
                                
                                inputMode="decimal" 
                                
                                value={ toText ( value [ code ] ) } 
                                
                                disabled={ disabled } 
                                
                                action={ event => onChange ( [ ...path, code ], event.target.value ) } />

                        </td>

                        <td className="pricing-table__actions">

                            <button 
                            
                                type="button" 
                                
                                className="icon" 
                                
                                disabled={ disabled } 
                                
                                title={ t ( "delete", "Delete" ) }
                                
                                onClick={ () => onChange ( path, omit ( value, code ) ) }>

                                <MdDeleteOutline />

                            </button>

                        </td>

                    </tr> ) }

                </tbody>

            </table>

        </div>

        <AddCountryRow 
            
            disabled={ disabled } 
            
            existing={ codes } 
            
            onAdd={ code => onChange ( path, { ...value, [ code ]: 0 } ) } />

    </div> );

},

/**
 * 
 * @returns one card per country so availability, free-delivery rules and brackets sit together
 * 
 */

CountryCards = ( { name, path, value, onChange, disabled } ) => { const { t } = useTranslation (),

    codes = Object.keys ( value ),

    template = value [ codes [ 0 ] ] || BLANK_COUNTRY;

    /** @returns */

    return ( <div className="pricing-group">

        <div className="pricing-group__title">

            { labelFor ( t, name ) }

        </div>

        <div className="country-cards">

            { codes.map ( code => <div className="country-card" key={ code }>

                <div className="country-card__title">

                    <span>

                        { t ( `delivery_pricing_country_${ code.toLowerCase () }`, code ) }

                    </span>

                    <button 
                    
                        type="button" 
                        
                        className="icon" 
                        
                        disabled={ disabled } 
                        
                        title={ t ( "delete", "Delete" ) }
                        
                        onClick={ () => onChange ( path, omit ( value, code ) ) }>

                        <MdDeleteOutline />

                    </button>

                </div>

                <PricingFields 
                
                    value={ value [ code ] } 
                    
                    path={ [ ...path, code ] } 
                    
                    onChange={ onChange } 
                    
                    disabled={ disabled } 
                    
                    nested />

            </div> ) }

        </div>

        <AddCountryRow 
            
            disabled={ disabled } 
            
            existing={ codes } 
            
            onAdd={ code => onChange ( path, { ...value, [ code ]: JSON.parse ( JSON.stringify ( template ) ) } ) } />

    </div> );

},

/**
 * 
 * @returns a country-code field plus an add button
 * 
 */

AddCountryRow = ( { disabled, existing, onAdd } ) => { const { t } = useTranslation (),

    [ code, setCode ] = useState ( "" ),

    next = normalizeCountryCode ( code ),

    add = () => {

        if ( ! isCountryCode ( next ) || existing.includes ( next ) ) return;

        onAdd ( next );

        setCode ( "" );

    };

    /** @returns */

    return ( <div className="pricing-add-country">

        <Input 
            
            type="text" 
            
            maxLength={ 2 } 
            
            placeholder={ t ( "delivery_pricing_country_code" ) } 
            
            value={ code } 
            
            disabled={ disabled } 
            
            action={ event => setCode ( event.target.value ) } 
            
            enterEvent={ event => { if ( event.key === "Enter" ) { event.preventDefault (); add (); } } } />

        <button type="button" className="pricing-table__add" disabled={ disabled || ! isCountryCode ( next ) } onClick={ add }>

            <MdAdd />

            <span>

                { t ( "delivery_pricing_add_country" ) }

            </span>

        </button>

    </div> );

},

/**
 * 
 * @returns the table-shaped price settings of one delivery method
 * 
 */

PricingFields = ( { value, path = [], onChange, disabled = false, nested = false } ) => { const { t } = useTranslation ();

    if ( ! isGroup ( value ) ) return null;

    const entries = Object.entries ( value ).map ( ( [ key, child ] ) => { const childPath = [ ...path, key ];

        if ( isSurchargeTable ( key, child ) ) return <TypeSurcharges 
        
            key={ key } name={ key } path={ childPath } value={ child } onChange={ onChange } disabled={ disabled } />;

        if ( isCountryPriceMap ( key, child ) ) return <CountryPriceMap 
        
            key={ key } name={ key } path={ childPath } value={ child } onChange={ onChange } disabled={ disabled } />;

        if ( isCountryGroup ( key, child ) ) return <CountryCards 
        
            key={ key } name={ key } path={ childPath } value={ child } onChange={ onChange } disabled={ disabled } />;

        if ( isRowList ( child ) ) return <RowList 
        
            key={ key } name={ key } path={ childPath } value={ child } onChange={ onChange } disabled={ disabled } framed={ ! nested } />;

        if ( Array.isArray ( child ) ) return <WordList 
        
            key={ key } name={ key } path={ childPath } value={ child } onChange={ onChange } disabled={ disabled } />;

        if ( isGroup ( child ) ) return ( <div className="pricing-group" key={ key }>

            <div className="pricing-group__title">

                { labelFor ( t, key ) }

            </div>

            <PricingFields value={ child } path={ childPath } onChange={ onChange } disabled={ disabled } nested />

        </div> );

        return <Leaf 
        
            key={ key } name={ key } path={ childPath } value={ child } onChange={ onChange } disabled={ disabled } />;

    } );

    /** @returns */

    return nested ? <div className="pricing-stack">{ entries }</div> : <>{ entries }</>;

};

/** @exports PricingFields */

export default PricingFields;
